import { apiClient, isHttpClientError } from "@/http";
import type { EpisodeStatus, EpisodeTerm } from "@/modules/dataset/public";
import { apiRoutes } from "./apiRoutes";
import type { MemberRef } from "./member";
import type {
  EpisodeInvalidReason,
  EpisodeSnapshot,
  EpisodeValidationStatus,
} from "./episodeReviewState";

/**
 * 에피소드 전역 리뷰(결과·결함·제외·표기·결함 수용·코멘트)를 저장하는 경계다.
 *
 * 리뷰는 데이터셋별 오버레이가 아니라 **에피소드 전역** 속성이다("A 에서 나쁜 데이터면 B 에서도
 * 나쁘다"). 저장 경로는 dataset 스코프(가시성 게이트)다. 판정은 이 에피소드를 공유하는 모든
 * 데이터셋에 반영된다. 제외는 별도 컬럼이 아니라 `Episode.status='excluded'` 로 표현한다.
 *
 * **판정 축은 서로 직교한다:** 결과(`task_failed`) · 결함 유형(`quality_tags`).
 * 실패여도 결함 태그가 비어 있을 수 있다(사유 미기록). 그래서
 * `quality_tags` 가 비어 있으면 **결함 미기록**이다. 성공이라는 뜻이 아니다.
 *
 * 쓰기는 **부분 갱신**이다. 필드마다 세 상태가 있다.
 * - 생략: 현재 값을 유지한다.
 * - 값: 그 값으로 바꾼다. 빈 목록(`quality_tags`·`terms`)은 비우기다.
 * - `comment: null`: 코멘트를 지운다. 다른 필드의 null 은 서버가 422 로 거절한다.
 * 계약 밖 필드(`outcome`·`intervention_tags` 등)도 422 다.
 *
 * 검토 완료는 `complete_review: true` 로만 기록한다. 수정 내용이 없어도 기록한다. 완료
 * 요청 없이 실패 여부·수동 결함·제외가 실제로 바뀌면 서버가 완료를 해제한다. 웹 리뷰 화면은
 * 저장과 완료를 한 동작으로 보낸다.
 *
 * Owns: 요청·응답 wire 타입, 코멘트 삭제 표현(`commentUpdateValue`), 저장 오류 판정.
 * Does not own: 무엇이 바뀌었는지의 판정(`episodeAnnotationDraft`), 저장 뒤 이동(리뷰 독).
 *
 * 계약(backend `service/episode/schema.py::EpisodeReviewUpdateReq`/`EpisodeReviewResp`)과 1:1.
 */

// 자유 어휘 한 건의 쓰기 형태다. 부여 경로(`source`)는 서버가 정한다. 그래서 보내지 않는다.
export type EpisodeTermUpdate = {
  // 서버가 정규화한다. 규칙 밖 키는 422 다.
  term_key: string;
  term_value: string;
  canonical_key: string | null;
  canonical_value: string | null;
};

// 리뷰 쓰기 payload 다. `expected_annotation_version` 만 필수다. 나머지는 바꿀 때만 담는다.
export type EpisodeReviewUpdate = {
  // 직전 조회에서 받은 낙관적 동시성 토큰이다. 현재 행과 다르면 서버가 아무것도 쓰지 않고
  // 409 를 반환한다(다른 클라이언트가 먼저 저장함).
  expected_annotation_version: string;
  // true 면 호출자와 지금 시각으로 검토 완료를 기록한다. 생략·false 는 완료 해제 명령이 아니다.
  complete_review?: boolean;
  // 실패 여부다. 결함과 직교하고, 제외 판정도 아니다. 실패 에피소드는 실패 인식 학습에
  // 정상적으로 쓴다. 그래서 서버가 `excluded` 를 자동으로 켜지 않는다.
  task_failed?: boolean;
  // 결함 유형 어휘의 SoT 는 GET /v1/review-options 의 `episode_quality_tags` 다. 서버는 이
  // 경로가 붙인(`manual`) 행만 바꾼다. 다른 경로가 붙인 결함은 빼고 보내도 남는다.
  quality_tags?: string[];
  // 자유 어휘다. 서버는 `manual` 행만 바꾼다. 다른 경로가 붙인 어휘는 빼고 보내도 남는다.
  terms?: EpisodeTermUpdate[];
  // 결함 수용이다. 검증이 무효가 아닌 에피소드를 새로 수용하면 409 `episode.not_invalid` 다.
  accepted_invalid?: boolean;
  // 코멘트다. null 이면 지운다. 값은 `commentUpdateValue` 로 만든다.
  comment?: string | null;
  // 제외 토글이다. true 면 status=excluded 가 된다. false 이고 현재 excluded 면 available 로
  // 복원한다. 학습이 이 Dataset 을 쓰는 동안 값을 바꾸면 409 `episode.training_locked` 다.
  //
  // 서버는 저장할 때 GCS replica 객체를 점 파일로 이름을 바꾼다. 그래서 학습의 파일 열거에서도
  // 실제로 빠진다. 객체를 수정하므로 다른 필드만 바꾸는 저장보다 오래 걸린다
  // (에피소드당 최대 30개 × 왕복 3회).
  excluded?: boolean;
};

// 저장 응답이다. 서버가 저장한 전역 리뷰 상태를 그대로 돌려준다. 요청 값을 낙관적으로 반영하지
// 말고 이 값을 쓴다. 서버가 no-op 이나 정규화 때문에 요청을 그대로 반영하지 않을 수 있다.
export type EpisodeReviewResp = {
  uuid: string;
  status: EpisodeStatus;
  validation_status: EpisodeValidationStatus;
  invalid_reason: EpisodeInvalidReason | null;
  task_failed: boolean;
  // 모든 부여 경로의 결함이다. 그래서 `[]` 를 보내도 비지 않을 수 있다.
  quality_tags: string[];
  terms: EpisodeTerm[];
  accepted_invalid: boolean;
  // 스냅샷은 수용·코멘트를 기록한 시점의 에피소드 상태다. 기록이 없으면 null 이다.
  // stale=true 는 그 뒤 파일·판정·진단이 바뀌었다는 뜻이다. null 은 비교할 기록이 없다는 뜻이다.
  accepted_invalid_snapshot: EpisodeSnapshot | null;
  accepted_invalid_stale: boolean | null;
  review_comment: string | null;
  review_comment_snapshot: EpisodeSnapshot | null;
  review_comment_stale: boolean | null;
  // 마지막으로 검토를 완료한 사람과 시각이다. 미검토이거나 완료가 해제되었으면 둘 다 null 이다.
  reviewed_by_member: MemberRef | null;
  reviewed_at: string | null;
  // 다음 저장에 보낼 토큰이다. 완료만 기록해도 새 값이 온다. no-op 이면 값이 바뀌지 않는다.
  annotation_version: string;
};

/** 에피소드 전역 리뷰를 부분 갱신한다. dataset_uuid 는 스코프와 가시성 게이트에 쓴다. */
export async function saveEpisodeReview(
  datasetUuid: string,
  episodeUuid: string,
  payload: EpisodeReviewUpdate,
): Promise<EpisodeReviewResp> {
  const response = await apiClient.put<EpisodeReviewResp>(
    apiRoutes.dataset.episodeReview(datasetUuid, episodeUuid),
    payload,
  );
  return response.data;
}

/**
 * 입력한 코멘트를 쓰기 값으로 바꾼다. 앞뒤 공백을 지운다. 비면 null(코멘트 삭제)이다.
 *
 * 서버는 길이를 공백을 지우기 전에 잰다. 그래서 끝의 공백 때문에 상한을 넘지 않게 여기서 지운다.
 */
export function commentUpdateValue(comment: string): string | null {
  const trimmed = comment.trim();
  return trimmed === "" ? null : trimmed;
}

// 제외 여부는 요청이 아니라 status 에서 파생한다. 서버가 정규화 때문에 요청을 그대로 반영하지
// 않을 수 있다.
export function isEpisodeExcluded(status: EpisodeStatus): boolean {
  return status === "excluded";
}

// 결함이 기록되어 있는지 판정한다. 결함 태그가 하나라도 있으면 true 다.
// **비어 있다고 "성공"은 아니다.** 실패 여부는 결과 축(`task_failed`)이 알려 준다.
// 이 헬퍼는 "결함을 기록했는가"만 알려 준다.
export function hasQualityIssue(qualityTags: readonly string[]): boolean {
  return qualityTags.length > 0;
}

// 낙관적 동시성 충돌이다(다른 클라이언트가 먼저 저장함). 서버에 부수효과는 없다.
// 처리 방법은 최신값을 다시 조회한 뒤 다시 저장하는 것이다. 자동 재시도나 병합은 하지 않는다
// (backend `episode.annotation_version_conflict`, 409).
export function isAnnotationVersionConflict(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "episode.annotation_version_conflict";
}

// 검증이 무효가 아닌 에피소드를 새로 수용하려 했다(409). 다른 곳에서 판정이 바뀌었을 수 있다.
export function isEpisodeNotInvalid(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "episode.not_invalid";
}

// 수용할 수 없는 사유의 에피소드를 새로 수용하려 했다(409). 지금은 채널 누락(`missing_channel`)이다.
// 파일을 채워 재검사해야 판정이 바뀐다. 다른 곳에서 사유가 바뀌었을 수 있다.
export function isInvalidReasonNotAcceptable(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "episode.invalid_reason_not_acceptable";
}
